import { generateJSON } from "@tiptap/html";
import { Fragment } from "@tiptap/pm/model";
import { type EditorState, Plugin, PluginKey } from "@tiptap/pm/state";
import type { EditorView } from "@tiptap/pm/view";
import { type Editor, Extension } from "@tiptap/react";
import type { FrontMatterParseResult } from "@workith/editor-schema";
import { frontMatterPositions, parseFrontMatter } from "@workith/editor-schema";
import { markdownToHtml } from "../lib/markdownToHtml";

export interface MarkdownPasteOptions {
  /** 이 편집기(레거시 문서 또는 첫 섹션)에서 문서 선두 front matter 변환을 허용할지 여부. */
  allowFrontMatter: boolean | (() => boolean);
}

function canInsertFrontMatter(permission: MarkdownPasteOptions["allowFrontMatter"]): boolean {
  return typeof permission === "function" ? permission() : permission;
}

/**
 * 붙여넣기 위치가 문서의 절대 시작인지 검사한다.
 * `pos === 1`이면 첫 top-level child의 내부 offset 0이므로 위치 조건은 이 하나로 충분하다.
 * 여기에 기존 카드가 없다는 조건까지 만족해야 변환 대상이 된다.
 */
function isAbsoluteDocumentStart(state: EditorState): boolean {
  const { selection, doc } = state;
  if (!selection.empty || selection.$from.pos !== 1) return false;
  return frontMatterPositions(doc).length === 0;
}

/**
 * 카드와 본문을 한 트랜잭션으로 넣어 붙여넣기 전체를 단일 undo 단계로 만든다.
 * 커서가 있던 빈 첫 블록은 일반 붙여넣기와 같이 삽입 내용으로 대체한다.
 */
function insertFrontMatterPaste(
  view: EditorView,
  editor: Editor,
  parsed: FrontMatterParseResult
): boolean {
  const body = parsed.body.trim()
    ? (generateJSON(markdownToHtml(parsed.body), editor.extensionManager.extensions, {
        preserveWhitespace: false,
      }).content ?? [])
    : [];
  const fragment = Fragment.fromArray(
    [parsed.frontMatter, ...body].map((json) => editor.schema.nodeFromJSON(json))
  );

  const firstBlock = view.state.doc.firstChild;
  const emptyFirstBlock =
    firstBlock?.isTextblock && firstBlock.content.size === 0 ? firstBlock : null;
  const tr = emptyFirstBlock
    ? view.state.tr.replaceWith(0, emptyFirstBlock.nodeSize, fragment)
    : view.state.tr.insert(0, fragment);
  view.dispatch(tr.scrollIntoView());
  return true;
}

/**
 * 순수 텍스트 붙여넣기 시 마크다운을 해석해 노드로 삽입하는 확장.
 * - text/html이 있는 붙여넣기(웹/워드/에디터 간)는 네이티브 처리에 위임
 * - 코드블록 안에서는 원문 그대로 유지
 * - 허용된 편집기의 문서 절대 시작에서는 YAML front matter를 카드로 변환
 */
export const MarkdownPaste = Extension.create<MarkdownPasteOptions>({
  name: "markdownPaste",

  addOptions() {
    return { allowFrontMatter: false };
  },

  addProseMirrorPlugins() {
    const editor = this.editor;
    const { allowFrontMatter } = this.options;

    return [
      new Plugin({
        key: new PluginKey("markdownPaste"),
        props: {
          handlePaste: (view, event) => {
            const clipboard = event.clipboardData;
            if (!clipboard) {
              return false;
            }

            const htmlData = clipboard.getData("text/html");
            const text = clipboard.getData("text/plain");

            if (!text.trim()) {
              return false;
            }

            // VSCode나 Obsidian 같은 코드 에디터에서 복사한 경우이거나,
            // 텍스트 내에 명확한 마크다운 문법(제목, 리스트, 인용, 코드블록, 테이블)이 포함된 경우
            // HTML 서식을 무시하고 마크다운으로 파싱한다.
            const isVsCode = clipboard.types.includes("vscode-editor-data");

            // 코드블록 안에서는 마크다운으로 해석하지 않고 원문 유지
            if (view.state.selection.$from.parent.type.spec.code) {
              return false;
            }

            // 일반 Markdown 휴리스틱보다, 또 text/html 위임 정책보다도 먼저 검사한다.
            // Orca처럼 마크다운 원문을 text/plain에 담으면서 렌더링 결과를 text/html에도
            // 싣는 에디터에서 복사해도 순수 텍스트(Obsidian)와 같은 카드 변환을 얻기 위함이다.
            // 경계·YAML·중복 키·최상위 타입 중 하나라도 어긋나면 null이 되어
            // 아래의 기존 text/html 위임·전체 텍스트 경로로 그대로 내려간다.
            const parsed =
              canInsertFrontMatter(allowFrontMatter) && isAbsoluteDocumentStart(view.state)
                ? parseFrontMatter(text)
                : null;

            if (parsed) {
              return insertFrontMatterPaste(view, editor, parsed);
            }

            if (htmlData && !isVsCode) {
              return false;
            }

            const isMarkdown = /^(#+\s|\*|-|\d+\.|>)\s|^\[[ xX]\]\s|`{3,}|^\|.*\|/m.test(text);

            if (!isVsCode && !isMarkdown) {
              return false;
            }

            const html = markdownToHtml(text);
            editor.commands.insertContent(html, {
              parseOptions: { preserveWhitespace: false },
            });

            return true;
          },
        },
      }),
    ];
  },
});
